/**
 * SearchTab — Player username search
 *
 * Debounced search input with results showing add/already-friend state.
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';
import type { PlayerSearchResult } from '@sdl/shared';
import { formatStacks } from './formatStacks';

interface SearchTabEvents {
  'player:add': { playerId: string };
  'player:unblock': { playerId: string };
  'player:select': { playerId: string };
  'search': { query: string };
}

const DEBOUNCE_MS = 700;

export class SearchTab extends EventEmitter<SearchTabEvents> {
  public element: HTMLElement;
  private inputEl!: HTMLInputElement;
  private resultsEl!: HTMLElement;
  private emptyEl!: HTMLElement;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private results: PlayerSearchResult[] = [];
  private pendingIds: Set<string> = new Set();

  constructor() {
    super();
    this.element = this.createElement();
  }

  setResults(results: PlayerSearchResult[]): void {
    this.results = results;
    this.renderResults();
  }

  /** The loaded search result for a player id (for enriching the player card). */
  getResult(playerId: string): PlayerSearchResult | undefined {
    return this.results.find(r => r.id === playerId);
  }

  markAsFriend(playerId: string): void {
    const r = this.results.find(r => r.id === playerId);
    if (r) {
      r.isFriend = true;
      this.pendingIds.delete(playerId);
      this.renderResults();
    }
  }

  markAsPending(playerId: string): void {
    this.pendingIds.add(playerId);
    this.renderResults();
  }

  /** After unblocking from a search row, flip it back to the ADD affordance. */
  markAsUnblocked(playerId: string): void {
    const r = this.results.find(r => r.id === playerId);
    if (r) {
      r.isBlocked = false;
      this.renderResults();
    }
  }

  destroy(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.element.remove();
    this.removeAllListeners();
  }

  private createElement(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'crew-tab-content crew-search-tab';

    el.innerHTML = `
      <div class="crew-search-bar">
        <svg class="crew-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" stroke-width="2">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input class="crew-search-input" type="text" placeholder="Search by username (min 3 chars)..." maxlength="30" autocomplete="off" spellcheck="false">
      </div>
      <div class="crew-search-results" style="display: none;"></div>
      <div class="crew-empty-state" style="display: none;">
        <div class="crew-empty-icon">🔍</div>
        <div class="crew-empty-title">FIND PLAYERS</div>
        <div class="crew-empty-text">Type a username to search</div>
      </div>
    `;

    this.inputEl = el.querySelector('.crew-search-input')!;
    this.resultsEl = el.querySelector('.crew-search-results')!;
    this.emptyEl = el.querySelector('.crew-empty-state')!;

    this.inputEl.addEventListener('input', () => {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        const query = this.inputEl.value.trim();
        if (query.length >= 3) {
          this.emit('search', { query });
        } else {
          this.results = [];
          this.renderResults();
        }
      }, DEBOUNCE_MS);
    });

    this.resultsEl.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const row = target.closest<HTMLElement>('.crew-search-row');
      if (!row) return;
      const playerId = row.dataset.playerId!;

      if (target.closest('.crew-search-add-btn')) {
        this.emit('player:add', { playerId });
        return;
      }
      if (target.closest('.crew-search-unblock-btn')) {
        this.emit('player:unblock', { playerId });
        return;
      }
      this.emit('player:select', { playerId });
    });

    // Show initial empty state
    this.emptyEl.style.display = 'flex';
    return el;
  }

  private renderResults(): void {
    this.resultsEl.innerHTML = '';

    if (this.results.length === 0 && this.inputEl.value.trim().length >= 3) {
      this.emptyEl.querySelector('.crew-empty-title')!.textContent = 'NO RESULTS';
      this.emptyEl.querySelector('.crew-empty-text')!.textContent = 'No players found with that name';
      this.emptyEl.style.display = 'flex';
      this.resultsEl.style.display = 'none';
      return;
    }

    if (this.results.length === 0) {
      this.emptyEl.querySelector('.crew-empty-title')!.textContent = 'FIND PLAYERS';
      this.emptyEl.querySelector('.crew-empty-text')!.textContent = 'Type a username to search';
      this.emptyEl.style.display = 'flex';
      this.resultsEl.style.display = 'none';
      return;
    }

    this.emptyEl.style.display = 'none';
    this.resultsEl.style.display = 'flex';

    for (const result of this.results) {
      const initial = result.username.charAt(0).toUpperCase();
      const avatarContent = result.picture
        ? `<img src="${result.picture}" alt="${initial}" class="crew-cell-avatar-img">`
        : `<span class="crew-cell-avatar-initial">${initial}</span>`;

      const row = document.createElement('div');
      row.className = 'crew-search-row';
      row.dataset.playerId = result.id;
      row.innerHTML = `
        <div class="crew-cell-avatar">
          ${avatarContent}
        </div>
        <div class="crew-cell-info">
          <div class="crew-cell-name">${result.username}</div>
          <div class="crew-cell-meta"><span class="crew-cell-stacks">$${formatStacks(result.stacks)}</span></div>
        </div>
        ${result.isBlocked
          ? `<button class="crew-search-unblock-btn">UNBLOCK</button>`
          : result.isFriend
            ? `<span class="crew-search-added">
                 <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00FF88" stroke-width="2.5">
                   <polyline points="20 6 9 17 4 12"/>
                 </svg>
               </span>`
            : this.pendingIds.has(result.id)
              ? `<span class="crew-search-pending">PENDING</span>`
              : `<button class="crew-search-add-btn">+ ADD</button>`
        }
      `;
      this.resultsEl.appendChild(row);
    }
  }
}
